import { apiRequest } from './client'
import { MOCK } from '../dev/mock'
import type {
  CloseDealPayload,
  CloseDealResponse,
  DealCallsResponse,
  DealDetailResponse,
  DealsListResponse,
  FunnelsResponse,
  MoveStageResponse,
} from './types'

export function fetchDeals(params: {
  stage?: string
  page?: number
  per_page?: number
} = {}): Promise<DealsListResponse> {
  const q = new URLSearchParams()
  if (params.stage) q.set('stage', params.stage)
  if (params.page) q.set('page', String(params.page))
  if (params.per_page) q.set('per_page', String(params.per_page))
  const qs = q.toString()
  return apiRequest<DealsListResponse>(
    `/api/miniapp/deals${qs ? `?${qs}` : ''}`,
  )
}

export function fetchDeal(dealId: number): Promise<DealDetailResponse> {
  return apiRequest<DealDetailResponse>(`/api/miniapp/deals/${dealId}`)
}

// In-deal call recordings (GET; MOCK handled by apiRequest's GET-mock branch).
export function fetchDealCalls(dealId: number): Promise<DealCallsResponse> {
  return apiRequest<DealCallsResponse>(`/api/miniapp/deals/${dealId}/calls`)
}

// All funnels + their non-terminal stages (cross-funnel move picker).
export function fetchFunnels(): Promise<FunnelsResponse> {
  if (MOCK) return Promise.resolve({ funnels: [] })
  return apiRequest<FunnelsResponse>(`/api/miniapp/deals/funnels`)
}

// Manual stage move (design StageTransitionSheet). `toPipeline`, when given and
// different from the deal's current funnel, makes it a cross-funnel move.
// MOCK → synthetic success.
export function moveDealStage(
  dealId: number,
  toStage: string,
  comment?: string,
  toPipeline?: string | null,
): Promise<MoveStageResponse> {
  if (MOCK) return Promise.resolve({ ok: true, from_stage: 'mock', to_stage: toStage })
  return apiRequest<MoveStageResponse>(`/api/miniapp/deals/${dealId}/stage`, {
    method: 'POST',
    body: { to_stage: toStage, to_pipeline: toPipeline ?? null, comment: comment ?? null },
  })
}

export interface SetClientNameResponse { contact_id: number | null; contact_name: string }

// Set/edit the deal's client (contact) name → writes to the Bitrix contact
// (created + attached if the deal has none). MOCK → synthetic success.
export function saveDealClientName(dealId: number, name: string): Promise<SetClientNameResponse> {
  if (MOCK) return Promise.resolve({ contact_id: 909, contact_name: name })
  return apiRequest<SetClientNameResponse>(`/api/miniapp/deals/${dealId}/client-name`, {
    method: 'PATCH',
    body: { name },
  })
}

export interface SetDealManagerResponse { manager_id: number; manager_name: string }

// Director changes the deal's responsible manager (CRM ASSIGNED_BY_ID).
// MOCK → synthetic success.
export function saveDealManager(dealId: number, managerId: number): Promise<SetDealManagerResponse> {
  if (MOCK) return Promise.resolve({ manager_id: managerId, manager_name: 'Тест Менеджер' })
  return apiRequest<SetDealManagerResponse>(`/api/miniapp/deals/${dealId}/manager`, {
    method: 'PATCH',
    body: { manager_id: managerId },
  })
}

// Close Won/Lost (design WonLostSheet). `amount` is absolute UZS.
export function closeDeal(dealId: number, payload: CloseDealPayload): Promise<CloseDealResponse> {
  if (MOCK) return Promise.resolve({ ok: true, kind: payload.kind, to_stage: payload.kind === 'won' ? 'WON' : 'LOSE' })
  return apiRequest<CloseDealResponse>(`/api/miniapp/deals/${dealId}/close`, {
    method: 'POST',
    body: {
      kind: payload.kind,
      amount: payload.amount ?? null,
      reason: payload.reason ?? null,
      note: payload.note ?? null,
    },
  })
}
